/**
 * Account AI keys card on the account-detail page (OCP-497).
 *
 * Operator/agent surface for account-scoped orboto AI API keys. Self-contained:
 * loads `GET /v1/admin/accounts/:id/ai-keys`. The operator can MINT a key on the
 * customer's behalf (the reveal-once value is shown once, to hand to the
 * customer over a secure channel - the key never lands anywhere else) and REVOKE
 * a key. This is the same route the ocp_*_account_ai_key MCP tools wrap, so an
 * agent and the UI drive the identical flow - incl. for self-hosted customers
 * where there is no Coolify tenant to inject into.
 */
import { useCallback, useEffect, useState } from 'react';

import { api, ApiError, readErrorKey } from '../lib/api';
import { fmtDateTime } from '../lib/format-time';

interface KeyRow {
  id: string;
  name: string | null;
  key_prefix: string;
  created_via: 'portal' | 'operator' | 'license';
  created_by: string;
  created_at: string;
  last_used_at: string | null;
  revoked_at: string | null;
  revoked_by: string | null;
  blocked_at: string | null;
  /** OCP-770 - final accounting of a revoked key (null while live). */
  final_accounting?: 'pending' | 'complete' | null;
  final_accounted_at?: string | null;
  status: 'active' | 'blocked' | 'revoked';
  gated: boolean;
}

interface ListResponse {
  account_id: string;
  gateway_configured: boolean;
  api_keys: KeyRow[];
}

interface Minted {
  id: string;
  name: string | null;
  key: string;
  key_prefix: string;
  created_at: string;
}

function fmtUtc(iso: string): string {
  return fmtDateTime(iso);
}

export function AccountAiKeysCard({ customerId }: { customerId: string }) {
  const [data, setData] = useState<ListResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [minted, setMinted] = useState<Minted | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const r = await api<ListResponse>(`/v1/admin/accounts/${customerId}/ai-keys`);
      setData(r);
    } catch (err) {
      setError(err instanceof ApiError ? `Load failed (HTTP ${err.status}).` : 'Load failed.');
    } finally {
      setLoading(false);
    }
  }, [customerId]);

  useEffect(() => {
    void load();
  }, [load]);

  async function mint(): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      const m = await api<Minted>(`/v1/admin/accounts/${customerId}/ai-keys`, {
        method: 'POST',
        body: {},
      });
      setMinted(m);
      void load();
    } catch (err) {
      if (err instanceof ApiError && readErrorKey(err.body) === 'gateway_unconfigured') {
        setError('The managed-AI gateway is not configured - set the ai.* settings first.');
      } else if (err instanceof ApiError && readErrorKey(err.body) === 'key_cap_reached') {
        setError('This account is at the key cap - revoke an unused key first.');
      } else {
        setError(err instanceof ApiError ? `Mint failed (HTTP ${err.status}).` : 'Mint failed.');
      }
    } finally {
      setBusy(false);
    }
  }

  async function revoke(keyId: string): Promise<void> {
    if (!window.confirm('Revoke this API key? It stops working immediately.')) return;
    try {
      await api(`/v1/admin/accounts/${customerId}/ai-keys/${keyId}`, { method: 'DELETE' });
      void load();
    } catch (err) {
      setError(err instanceof ApiError ? `Revoke failed (HTTP ${err.status}).` : 'Revoke failed.');
    }
  }

  const liveKeys = (data?.api_keys ?? []).filter((k) => k.status !== 'revoked');
  // OCP-770 - revoked keys whose last usage the sweep is still charging.
  const settlingKeys = (data?.api_keys ?? []).filter(
    (k) => k.status === 'revoked' && k.final_accounting === 'pending',
  );

  return (
    <div className="rounded-lg border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-4 shadow-sm">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2 className="text-sm font-semibold text-gray-900 dark:text-gray-100">orboto AI API keys</h2>
        {data?.gateway_configured && (
          <button
            type="button"
            data-testid="account-ai-key-mint"
            disabled={busy}
            onClick={() => void mint()}
            className="rounded bg-brand-500 px-2.5 py-1 text-xs font-medium text-white hover:bg-brand-600 disabled:opacity-50"
          >
            {busy ? 'Minting…' : '+ Mint key'}
          </button>
        )}
      </div>

      {error && <div className="mt-2 text-xs text-red-700 dark:text-red-300">{error}</div>}

      {settlingKeys.length > 0 && (
        <div
          data-testid="account-ai-keys-final-accounting"
          className="mt-2 rounded border border-sky-200 dark:border-sky-900/50 bg-sky-50 dark:bg-sky-900/30 px-3 py-2 text-xs text-sky-900 dark:text-sky-200"
        >
          Final accounting pending for revoked{' '}
          {settlingKeys.map((k) => `${k.key_prefix}…`).join(', ')}: blocked at the gateway, the
          usage sweep charges the remaining usage and deletes the key within about 25 minutes of the
          revocation.
        </div>
      )}

      {loading && !data && <div className="mt-3 text-sm text-gray-500">Loading…</div>}

      {data && !data.gateway_configured && (
        <div className="mt-3 rounded border border-amber-200 dark:border-amber-900/50 bg-amber-50 dark:bg-amber-900/30 px-3 py-2 text-xs text-amber-900 dark:text-amber-200">
          Managed-AI gateway is not configured; account AI keys are unavailable.
        </div>
      )}

      {minted && (
        <div
          data-testid="account-ai-key-reveal"
          className="mt-3 rounded border border-amber-300 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/30 p-3"
        >
          <p className="text-xs font-medium text-amber-900 dark:text-amber-200">
            New key - shown once. Hand it to the customer over a secure channel; it is never shown
            again.
          </p>
          <div className="mt-2 flex items-stretch gap-2">
            <code
              data-testid="account-ai-key-reveal-value"
              className="min-w-0 flex-1 overflow-x-auto rounded bg-gray-900 px-2 py-1.5 text-xs text-gray-100"
            >
              {minted.key}
            </code>
            <button
              type="button"
              onClick={() => {
                if (typeof navigator !== 'undefined' && navigator.clipboard) {
                  void navigator.clipboard.writeText(minted.key);
                }
              }}
              className="rounded bg-brand-500 px-2.5 py-1.5 text-xs font-medium text-white hover:bg-brand-600"
            >
              Copy
            </button>
            <button
              type="button"
              data-testid="account-ai-key-reveal-dismiss"
              onClick={() => setMinted(null)}
              className="rounded border border-gray-300 dark:border-gray-700 px-2.5 py-1.5 text-xs text-gray-700 dark:text-gray-300"
            >
              Dismiss
            </button>
          </div>
        </div>
      )}

      {data &&
        data.gateway_configured &&
        (liveKeys.length === 0 ? (
          <div className="mt-3 rounded border border-dashed border-gray-300 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/40 px-3 py-4 text-sm text-gray-500">
            No account AI keys.
          </div>
        ) : (
          <div className="mt-3 overflow-x-auto">
            <table className="w-full min-w-[720px] text-sm">
              <thead className="bg-gray-50 dark:bg-gray-800 text-left text-xs uppercase text-gray-500">
                <tr>
                  <th className="px-3 py-2">Name</th>
                  <th className="px-3 py-2">Key</th>
                  <th className="px-3 py-2">Via</th>
                  <th className="px-3 py-2">Status</th>
                  <th className="px-3 py-2">Last used</th>
                  <th className="px-3 py-2 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                {liveKeys.map((k) => (
                  <tr key={k.id} data-testid={`account-ai-key-row-${k.id}`}>
                    <td className="px-3 py-2 font-medium text-gray-900 dark:text-gray-100">
                      {k.name ?? '—'}
                    </td>
                    <td className="px-3 py-2 font-mono text-xs">{k.key_prefix}…</td>
                    <td className="px-3 py-2 text-xs text-gray-500">{k.created_via}</td>
                    <td className="px-3 py-2 text-xs">
                      {k.gated ? (
                        <span className="rounded bg-red-100 dark:bg-red-900/30 px-2 py-0.5 text-xs font-medium text-red-800 dark:text-red-300">
                          gated
                        </span>
                      ) : (
                        <span className="rounded bg-emerald-100 dark:bg-emerald-900/30 px-2 py-0.5 text-xs font-medium text-emerald-800 dark:text-emerald-300">
                          active
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-xs text-gray-500">
                      {k.last_used_at ? fmtUtc(k.last_used_at) : 'never'}
                    </td>
                    <td className="px-3 py-2 text-right">
                      <button
                        type="button"
                        data-testid={`account-ai-key-revoke-${k.id}`}
                        onClick={() => void revoke(k.id)}
                        className="text-xs text-red-700 dark:text-red-300 hover:underline"
                      >
                        Revoke
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ))}
    </div>
  );
}
